import { customColorTones } from './pdf-renderer/custom-color-tones';

/**
 * Famílias de cor da caixa de destaque (item CALLOUT) e da grade de indicadores (item
 * STATS), as duas em `configs.calloutColor`.
 *
 * O normal é um NOME: o par tinta/fundo de cada família é fixo, o mesmo do CSS do
 * PDF (`items/callout.css` e `items/stats.css` no PdfEngine, copiados em
 * `pdf-renderer/styles.ts`) e do app, para o texto ler bem impresso em qualquer
 * combinação. O editor desenha os cards com estes pares para o usuário ver o bloco
 * como ele sai na folha.
 * Também vale uma cor PERSONALIZADA (`#rrggbb`, o arco-íris do menu): os tons saem da
 * mesma conta do PDF (`pdf-renderer/custom-color-tones.ts`) e a opção tem a chave `custom`.
 * Ausente ou fora da lista => azul, como no renderer.
 */
export type CalloutColorKey = 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';

export interface CalloutColorOption {
    key: CalloutColorKey | 'custom';
    /** Cor do título (caixa alta) e do ponto do seletor. */
    ink: string;
    /** Fundo tonal da caixa. */
    fill: string;
    /** Cor cheia da família, usada no chip do rail e na lista de cores. */
    accent: string;
    /** Grade de indicadores: cor do número nos estilos suave e contorno. */
    statNumber: string;
    /** Grade de indicadores: cor do número no estilo colorido (sobre o `fill`). */
    statInk: string;
    /** Grade de indicadores: filete do estilo linha. */
    statLine: string;
    /** Destaque no estilo contorno: tom da borda. */
    line: string;
    /** Tabela preenchida: texto do cabeçalho sobre o `statNumber` (famílias: branco). */
    onAccent: string;
    labelKey: string;
}

export const CALLOUT_COLORS: readonly (CalloutColorOption & { key: CalloutColorKey })[] = [
    {
        key: 'blue',
        ink: '#1d5fb8',
        fill: '#eaf2ff',
        accent: '#2a89ef',
        statNumber: '#2479e0',
        statInk: '#1d5fb8',
        statLine: '#2a89ef',
        line: '#b9d3f7',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorBlue',
    },
    {
        key: 'green',
        ink: '#1e7a46',
        fill: '#ecf9f1',
        accent: '#15be78',
        statNumber: '#139a63',
        statInk: '#1e7a46',
        statLine: '#15be78',
        line: '#b4e6cf',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorGreen',
    },
    {
        key: 'orange',
        ink: '#b4561a',
        fill: '#fff5e8',
        accent: '#ff850a',
        statNumber: '#e06f06',
        statInk: '#b4561a',
        statLine: '#ff850a',
        line: '#f8d3ad',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorOrange',
    },
    {
        key: 'red',
        ink: '#b42323',
        fill: '#fdeded',
        accent: '#e04f4f',
        statNumber: '#d64545',
        statInk: '#b42323',
        statLine: '#e04f4f',
        line: '#f2bcbc',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorRed',
    },
    {
        key: 'purple',
        ink: '#5b3fbf',
        fill: '#f1edfd',
        accent: '#6366f1',
        statNumber: '#5457e6',
        statInk: '#5b3fbf',
        statLine: '#6366f1',
        line: '#cfc7f2',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorPurple',
    },
    {
        key: 'gray',
        ink: '#4b5563',
        fill: '#f3f4f6',
        accent: '#9ca3af',
        statNumber: '#4b5563',
        statInk: '#374151',
        statLine: '#9ca3af',
        line: '#d1d5db',
        onAccent: '#ffffff',
        labelKey: 'document.edit.calloutCard.colorGray',
    },
];

export const DEFAULT_CALLOUT_COLOR: CalloutColorKey = 'blue';

/**
 * Resolve a família a partir do valor gravado: uma das prontas, a personalizada (`#rrggbb`)
 * ou, desconhecido ou ausente, o azul.
 */
export function resolveCalloutColor(raw: string | null | undefined): CalloutColorOption {
    const key = raw?.trim().toLowerCase();
    return CALLOUT_COLORS.find(c => c.key === key) ?? customCalloutColor(key) ?? CALLOUT_COLORS[0];
}

/** Opção da cor personalizada, com os tons derivados do hex como no PDF; inválida => null. */
export function customCalloutColor(raw: string | null | undefined): CalloutColorOption | null {
    const tones = customColorTones(raw);
    if (!tones) return null;
    return {
        key: 'custom',
        ink: tones.ink,
        fill: tones.fill,
        accent: tones.accent,
        statNumber: tones.number,
        statInk: tones.ink,
        statLine: tones.accent,
        line: tones.line,
        onAccent: tones.onNumber,
        labelKey: 'document.edit.calloutCard.colorCustom',
    };
}

/**
 * Estilo da caixa de destaque (`configs.calloutStyle`), o mesmo do PDF (`items/callout.css`):
 * preenchida (o de sempre), com filete (fundo + barra à esquerda), contorno (fundo branco e
 * borda) e citação (só a barra, sem fundo, texto em itálico). Ausente ou desconhecido => fill.
 */
export type CalloutStyleKey = 'fill' | 'stripe' | 'outline' | 'quote';

export interface CalloutStyleOption {
    key: CalloutStyleKey;
    labelKey: string;
}

export const CALLOUT_STYLES: readonly CalloutStyleOption[] = [
    { key: 'fill', labelKey: 'document.edit.calloutCard.styleFill' },
    { key: 'stripe', labelKey: 'document.edit.calloutCard.styleStripe' },
    { key: 'outline', labelKey: 'document.edit.calloutCard.styleOutline' },
    { key: 'quote', labelKey: 'document.edit.calloutCard.styleQuote' },
];

export function resolveCalloutStyle(raw: string | null | undefined): CalloutStyleKey {
    const key = raw?.trim().toLowerCase();
    return CALLOUT_STYLES.find(s => s.key === key)?.key ?? 'fill';
}
